import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isAuth } from './store/actions/users';
import { Loader } from './utils/tool';
import RouteGuard from './hoc/routeGuard';
import AdminRouteGuard from './hoc/adminRouteGuard';
import Header from './components/navigation/header';
import MainLayout from './hoc/mainlayout';
import NotFound from './components/not-found';
import Unauthorized from './components/unauthorized/unauthorized';
import EditCountryLocation from './utils/edit/editCountryLocation';

// Lazy load components
const Auth = lazy(() => import('./components/auth'));
const Dashboard = lazy(() => import('./components/dashboard'));
const MainDashboard = lazy(() => import('./components/dashboard/main'));
const ForgetPassword = lazy(() => import('./components/auth/forget-password'));
const ResetPassword = lazy(() => import('./components/auth/reset-password'));
const AddUser = lazy(() => import('./components/admin/user/register-new-user'));
const GetUsers = lazy(() => import('./components/admin/user/get-users'));
const AddCountry = lazy(() => import('./components/admin/country/add-country'));
const ViewCountry = lazy(() => import('./components/admin/country/view-country'));
const AddPackage = lazy(() => import('./components/admin/packageType/add-package'));
const ViewPackage = lazy(() => import('./components/admin/packageType/view-package'));
const AddVacationType = lazy(() => import('./components/admin/vacationType/add-vacation'));
const ViewVacationType = lazy(() => import('./components/admin/vacationType/view-vacation'));
const AddCountryLocation = lazy(() => import('./components/admin/countryLocation/add-country'));
const ViewCountryLocation = lazy(() => import('./components/admin/countryLocation/view-country'));
const AddNewPackages = lazy(() => import('./components/admin/addPackage/add-new-package'));
const ViewPackages = lazy(() => import('./components/admin/addPackage/view-new-package'));
const ViewPackageOfSpecificId = lazy(() => import('./utils/view-package-data'));
const AddFeatures = lazy(() => import('./components/admin/features/add-features'));
const ViewFeatures = lazy(() => import('./components/admin/features/view-features'));
const AddBlogs = lazy(() => import('./components/admin/blogs/add-blogs'));
const ViewBlogs = lazy(() => import('./components/admin/blogs/view-blogs'));
const AccessControl = lazy(() => import('./components/admin/leads/accessControl'));
const Offers = lazy(() => import('./components/admin/offers/offers'));
const EditOffers = lazy(() => import('./components/admin/offers/editOffers'));

const AddBookingData = lazy(() => import('./components/admin/booking/addBooking'));
const ViewBooking = lazy(() => import('./components/admin/booking/viewBooking'));
const ViewSpecificBlog = lazy(() => import('./components/admin/blogs/view.specified.blog'));


const EditViewPackage = lazy(() => import('./utils/edit/packageTypeEdit'));

const Router = () => {
    const user = useSelector((state) => state.users);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        dispatch(isAuth());
    }, [dispatch]);

    useEffect(() => {
        if (user.auth !== null) {
            setLoading(false);
        }
    }, [user.auth]);

    const header = useMemo(() => <Header />, []);
    const layout = useMemo(() => <MainLayout />, []);

    return (
        <BrowserRouter>
            {loading ? <Loader /> : (
                <>
                    {header}
                    {layout}
                    <Suspense fallback={<Loader />}>
                        <Routes>
                            <Route path="/" element={<Auth />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/unauthorized" element={<Unauthorized />} />
                            <Route path="*" element={<NotFound />} />
                            <Route path="/dashboard" element={<RouteGuard><Dashboard /></RouteGuard>}>
                                <Route index element={<MainDashboard />} />
                                <Route path="add-user" element={<AdminRouteGuard><AddUser /></AdminRouteGuard>} />
                                <Route path="get-users" element={<AdminRouteGuard><GetUsers /></AdminRouteGuard>} />
                                <Route path="add-country" element={<AddCountry />} />
                                <Route path="get-country" element={<ViewCountry />} />
                                <Route path="add-package" element={<AddPackage />} />
                                <Route path="view-package" element={<ViewPackage />} />
                                <Route path="view-package/:id" element={<EditViewPackage />} />
                                <Route path="add-vacation-type" element={<AddVacationType />} />
                                <Route path="view-vacation-type" element={<ViewVacationType />} />
                                <Route path="add-country-location" element={<AddCountryLocation />} />
                                <Route path="view-country-location" element={<ViewCountryLocation />} />
                                <Route path="view-country-location/:id" element={<EditCountryLocation />} />
                                <Route path="add-new-package" element={<AddNewPackages />} />
                                <Route path="view-new-package" element={<ViewPackages />} />
                                <Route path="view-new-package/:id" element={<ViewPackageOfSpecificId />} />
                                <Route path="view-blogs/:id" element={<ViewSpecificBlog />} />
                                <Route path="add-features" element={<AddFeatures />} />
                                <Route path="view-features" element={<ViewFeatures />} />
                                <Route path="add-blogs" element={<AddBlogs />} />
                                <Route path="view-blogs" element={<ViewBlogs />} />
                                <Route path="view-leads" element={<AccessControl />} />
                                <Route path="create-offers" element={<Offers />} />
                                <Route path="edit-offers" element={<EditOffers />} />
                                <Route path="addBooking" element={<AddBookingData />} />
                                <Route path="viewBooking" element={<ViewBooking />} />
                            </Route>
                            <Route path="/forget-password" element={<ForgetPassword />} />
                        </Routes>
                    </Suspense>
                </>
            )}
        </BrowserRouter>
    );
};

export default Router;
